import { useState } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  TrashIcon,
  PlayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ChevronDownIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const AUTH_TYPES = [
  { id: 'none', name: 'No Auth' },
  { id: 'basic', name: 'Basic Auth' },
  { id: 'bearer', name: 'Bearer Token' },
  { id: 'api_key', name: 'API Key' }
];

const METHOD_COLORS = {
  GET: 'bg-emerald-500',
  POST: 'bg-amber-500',
  PUT: 'bg-blue-500',
  PATCH: 'bg-purple-500',
  DELETE: 'bg-red-500'
};

export default function RestApiSource({ companyId, onSuccess, onBack }) {
  const [name, setName] = useState('');
  const [method, setMethod] = useState('GET');
  const [url, setUrl] = useState('');
  const [activeTab, setActiveTab] = useState('params');
  
  // Params
  const [queryParams, setQueryParams] = useState([{ key: '', value: '', enabled: true }]);
  const [pathParams, setPathParams] = useState([{ key: '', value: '', enabled: true }]);
  
  // Headers
  const [headers, setHeaders] = useState([
    { key: 'Content-Type', value: 'application/json', enabled: true },
    { key: '', value: '', enabled: true }
  ]);
  
  // Auth
  const [authType, setAuthType] = useState('none');
  const [authConfig, setAuthConfig] = useState({
    username: '',
    password: '',
    token: '',
    apiKeyName: '',
    apiKeyValue: '',
    apiKeyIn: 'header'
  });
  const [showPassword, setShowPassword] = useState(false);
  
  // Body
  const [bodyType, setBodyType] = useState('json');
  const [bodyContent, setBodyContent] = useState('{\n  \n}');
  
  // Response & State
  const [response, setResponse] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [step, setStep] = useState(1); // 1: config, 2: preview
  
  const updateParam = (list, setList, index, field, value) => {
    const updated = [...list];
    updated[index][field] = value;
    setList(updated);
    
    // Add new empty row if last row is being filled
    if (index === list.length - 1 && (updated[index].key || updated[index].value)) {
      setList([...updated, { key: '', value: '', enabled: true }]);
    }
  };
  
  const removeParam = (list, setList, index) => {
    if (list.length > 1) {
      setList(list.filter((_, i) => i !== index));
    }
  };
  
  const buildUrl = () => {
    let finalUrl = url;
    
    // Replace path params
    pathParams.filter(p => p.enabled && p.key && p.value).forEach(param => {
      finalUrl = finalUrl.replace(`:${param.key}`, param.value);
      finalUrl = finalUrl.replace(`{${param.key}}`, param.value);
    });
    
    // Add query params
    const enabledQueryParams = queryParams.filter(p => p.enabled && p.key);
    if (enabledQueryParams.length > 0) {
      const searchParams = new URLSearchParams();
      enabledQueryParams.forEach(p => searchParams.append(p.key, p.value));
      finalUrl += (finalUrl.includes('?') ? '&' : '?') + searchParams.toString();
    }
    
    return finalUrl;
  };
  
  const buildHeaders = () => {
    const result = {};
    headers.filter(h => h.enabled && h.key).forEach(h => {
      result[h.key] = h.value;
    });
    
    // Add auth headers
    if (authType === 'basic') {
      const credentials = btoa(`${authConfig.username}:${authConfig.password}`);
      result['Authorization'] = `Basic ${credentials}`;
    } else if (authType === 'bearer') {
      result['Authorization'] = `Bearer ${authConfig.token}`;
    } else if (authType === 'api_key' && authConfig.apiKeyIn === 'header') {
      result[authConfig.apiKeyName] = authConfig.apiKeyValue;
    }
    
    return result;
  };
  
  const handleTest = async () => {
    if (!url) {
      toast.error('Please enter a URL');
      return;
    }
    
    setIsTesting(true);
    setResponse(null);
    
    try {
      const testConfig = {
        method,
        url: buildUrl(),
        headers: buildHeaders(),
        body: ['POST', 'PUT', 'PATCH'].includes(method) ? bodyContent : undefined
      };
      
      const result = await api.post('/data-sources/test-api', testConfig);
      setResponse(result.data);
      
      if (result.data.success) {
        toast.success('API request successful!');
        
        // If response has data, show preview
        if (result.data.data) {
          setPreview(result.data);
        }
      } else {
        toast.error(result.data.error || 'Request failed');
      }
    } catch (error) {
      console.error('API test error:', error);
      setResponse({
        success: false,
        status: error.response?.status || 0,
        error: error.response?.data?.error || error.message
      });
      toast.error('Failed to test API');
    } finally {
      setIsTesting(false);
    }
  };
  
  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name for the data source');
      return;
    }
    
    if (!url) {
      toast.error('Please enter a URL');
      return;
    }
    
    setIsCreating(true);
    
    try {
      const connectionConfig = {
        method,
        url,
        headers: buildHeaders(),
        queryParams: queryParams.filter(p => p.enabled && p.key),
        pathParams: pathParams.filter(p => p.enabled && p.key),
        authType,
        authConfig: authType !== 'none' ? authConfig : undefined,
        body: ['POST', 'PUT', 'PATCH'].includes(method) ? bodyContent : undefined
      };
      
      const result = await api.post('/data-sources', {
        name,
        type: 'api',
        companyId,
        connectionConfig
      });
      
      onSuccess(result.data.dataSource);
    } catch (error) {
      console.error('Create error:', error);
      toast.error(error.response?.data?.error || 'Failed to create data source');
    } finally {
      setIsCreating(false);
    }
  };
  
  const tabs = [
    { id: 'params', name: 'Params' },
    { id: 'auth', name: 'Authorization' },
    { id: 'headers', name: 'Headers' },
    { id: 'body', name: 'Body', disabled: !['POST', 'PUT', 'PATCH'].includes(method) }
  ];
  
  return (
    <div className="space-y-6">
      {/* Name input */}
      <div className="card p-4">
        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
          Data Source Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="input"
          placeholder="My API Data Source"
        />
      </div>
      
      {/* URL Bar */}
      <div className="card p-4">
        <div className="flex gap-2">
          {/* Method selector */}
          <div className="relative">
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className={`appearance-none pl-3 pr-8 py-2.5 rounded-lg font-medium text-white cursor-pointer ${METHOD_COLORS[method]}`}
            >
              {HTTP_METHODS.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-white pointer-events-none" />
          </div>
          
          {/* URL input */}
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="input flex-1 font-mono text-sm"
            placeholder="https://api.example.com/data/:id"
          />
          
          {/* Send button */}
          <button
            onClick={handleTest}
            disabled={isTesting}
            className="btn-primary px-6"
          >
            {isTesting ? (
              <ArrowPathIcon className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <PlayIcon className="w-5 h-5" />
                Send
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="card overflow-hidden">
        <div className="flex border-b border-surface-200 dark:border-surface-700">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => !tab.disabled && setActiveTab(tab.id)}
              disabled={tab.disabled}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : tab.disabled
                    ? 'border-transparent text-surface-400 cursor-not-allowed'
                    : 'border-transparent text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </div>
        
        <div className="p-4">
          {/* Params Tab */}
          {activeTab === 'params' && (
            <div className="space-y-4">
              {/* Query Params */}
              <div>
                <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  Query Parameters
                </h4>
                <div className="space-y-2">
                  {queryParams.map((param, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input
                        type="checkbox"
                        checked={param.enabled}
                        onChange={(e) => updateParam(queryParams, setQueryParams, i, 'enabled', e.target.checked)}
                        className="rounded"
                      />
                      <input
                        type="text"
                        value={param.key}
                        onChange={(e) => updateParam(queryParams, setQueryParams, i, 'key', e.target.value)}
                        className="input flex-1"
                        placeholder="Key"
                      />
                      <input
                        type="text"
                        value={param.value}
                        onChange={(e) => updateParam(queryParams, setQueryParams, i, 'value', e.target.value)}
                        className="input flex-1"
                        placeholder="Value"
                      />
                      <button
                        onClick={() => removeParam(queryParams, setQueryParams, i)}
                        className="p-2 text-surface-400 hover:text-red-500"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Path Params */}
              <div>
                <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  Path Parameters <span className="text-surface-400 font-normal">(use :param or {'{param}'} in URL)</span>
                </h4>
                <div className="space-y-2">
                  {pathParams.map((param, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input
                        type="checkbox"
                        checked={param.enabled}
                        onChange={(e) => updateParam(pathParams, setPathParams, i, 'enabled', e.target.checked)}
                        className="rounded"
                      />
                      <input
                        type="text"
                        value={param.key}
                        onChange={(e) => updateParam(pathParams, setPathParams, i, 'key', e.target.value)}
                        className="input flex-1"
                        placeholder="Parameter name"
                      />
                      <input
                        type="text"
                        value={param.value}
                        onChange={(e) => updateParam(pathParams, setPathParams, i, 'value', e.target.value)}
                        className="input flex-1"
                        placeholder="Value"
                      />
                      <button
                        onClick={() => removeParam(pathParams, setPathParams, i)}
                        className="p-2 text-surface-400 hover:text-red-500"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Auth Tab */}
          {activeTab === 'auth' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  Authorization Type
                </label>
                <select
                  value={authType}
                  onChange={(e) => setAuthType(e.target.value)}
                  className="input"
                >
                  {AUTH_TYPES.map(auth => (
                    <option key={auth.id} value={auth.id}>{auth.name}</option>
                  ))}
                </select>
              </div>
              
              {authType === 'basic' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                      Username
                    </label>
                    <input
                      type="text"
                      value={authConfig.username}
                      onChange={(e) => setAuthConfig({ ...authConfig, username: e.target.value })}
                      className="input"
                      placeholder="Username"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={authConfig.password}
                        onChange={(e) => setAuthConfig({ ...authConfig, password: e.target.value })}
                        className="input pr-10"
                        placeholder="Password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400"
                      >
                        {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {authType === 'bearer' && (
                <div>
                  <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                    Token
                  </label>
                  <input
                    type="text"
                    value={authConfig.token}
                    onChange={(e) => setAuthConfig({ ...authConfig, token: e.target.value })}
                    className="input font-mono text-sm"
                    placeholder="Enter your token"
                  />
                </div>
              )}
              
              {authType === 'api_key' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                        Key Name
                      </label>
                      <input
                        type="text"
                        value={authConfig.apiKeyName}
                        onChange={(e) => setAuthConfig({ ...authConfig, apiKeyName: e.target.value })}
                        className="input"
                        placeholder="X-API-Key"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                        Key Value
                      </label>
                      <input
                        type="text"
                        value={authConfig.apiKeyValue}
                        onChange={(e) => setAuthConfig({ ...authConfig, apiKeyValue: e.target.value })}
                        className="input font-mono text-sm"
                        placeholder="your-api-key"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                      Add to
                    </label>
                    <select
                      value={authConfig.apiKeyIn}
                      onChange={(e) => setAuthConfig({ ...authConfig, apiKeyIn: e.target.value })}
                      className="input"
                    >
                      <option value="header">Header</option>
                      <option value="query">Query Params</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Headers Tab */}
          {activeTab === 'headers' && (
            <div className="space-y-2">
              {headers.map((header, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input
                    type="checkbox"
                    checked={header.enabled}
                    onChange={(e) => updateParam(headers, setHeaders, i, 'enabled', e.target.checked)}
                    className="rounded"
                  />
                  <input
                    type="text"
                    value={header.key}
                    onChange={(e) => updateParam(headers, setHeaders, i, 'key', e.target.value)}
                    className="input flex-1"
                    placeholder="Header name"
                  />
                  <input
                    type="text"
                    value={header.value}
                    onChange={(e) => updateParam(headers, setHeaders, i, 'value', e.target.value)}
                    className="input flex-1"
                    placeholder="Value"
                  />
                  <button
                    onClick={() => removeParam(headers, setHeaders, i)}
                    className="p-2 text-surface-400 hover:text-red-500"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Body Tab */}
          {activeTab === 'body' && (
            <div className="space-y-4">
              <div className="flex gap-4">
                {['json', 'text', 'form-data'].map(type => (
                  <label key={type} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="bodyType"
                      value={type}
                      checked={bodyType === type}
                      onChange={(e) => setBodyType(e.target.value)}
                      className="text-primary-600"
                    />
                    <span className="text-sm text-surface-700 dark:text-surface-300 capitalize">
                      {type === 'form-data' ? 'Form Data' : type.toUpperCase()}
                    </span>
                  </label>
                ))}
              </div>
              
              <textarea
                value={bodyContent}
                onChange={(e) => setBodyContent(e.target.value)}
                className="input font-mono text-sm h-48 resize-none"
                placeholder={bodyType === 'json' ? '{\n  "key": "value"\n}' : 'Enter body content...'}
              />
            </div>
          )}
        </div>
      </div>
      
      {/* Response Section */}
      {response && (
        <div className="card overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-surface-200 dark:border-surface-700">
            <div className="flex items-center gap-3">
              {response.success ? (
                <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
              ) : (
                <ExclamationCircleIcon className="w-5 h-5 text-red-500" />
              )}
              <span className="font-medium text-surface-900 dark:text-white">
                Response
              </span>
              <span className={`text-sm px-2 py-0.5 rounded ${
                response.status >= 200 && response.status < 300
                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
              }`}>
                {response.status}
              </span>
              {response.duration && (
                <span className="text-sm text-surface-500">{response.duration}ms</span>
              )}
            </div>
          </div>
          
          <div className="p-4">
            {response.error ? (
              <div className="text-red-600 dark:text-red-400">{response.error}</div>
            ) : (
              <pre className="text-sm text-surface-700 dark:text-surface-300 overflow-x-auto bg-surface-50 dark:bg-surface-800 p-4 rounded-lg max-h-64">
                {JSON.stringify(response.data, null, 2)}
              </pre>
            )}
          </div>
          
          {/* Preview schema if data available */}
          {response.success && response.schema && (
            <div className="p-4 border-t border-surface-200 dark:border-surface-700">
              <h4 className="font-medium text-surface-900 dark:text-white mb-3">Detected Schema</h4>
              <div className="flex flex-wrap gap-2">
                {response.schema.columns?.map((col, i) => (
                  <div key={i} className="bg-surface-100 dark:bg-surface-800 rounded-lg px-3 py-2 text-sm">
                    <span className="text-surface-900 dark:text-white font-medium">{col.name}</span>
                    <span className="text-surface-500 ml-2">{col.type}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Action buttons */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="btn-secondary">
          Back
        </button>
        <button 
          onClick={handleCreate} 
          disabled={isCreating || !name.trim() || !url}
          className="btn-primary"
        >
          {isCreating ? (
            <>
              <ArrowPathIcon className="w-5 h-5 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <CheckCircleIcon className="w-5 h-5" />
              Create Data Source
            </>
          )}
        </button>
      </div>
    </div>
  );
}

